import { Button, Col, Icon, Input, Row, Sidepanel, Switch, Text } from 'components';
import {
  Api,
  POLL_DEFAULT_REWARD_POINTS,
  POLL_MAX_OPTIONS,
  POLL_MIN_OPTIONS,
  POLL_TYPE,
  theme
} from 'lib';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ContentHeader } from 'screens/Admin/components';
import PollGamePicker from './PollGamePicker';
import PollPlayerPicker from './PollPlayerPicker';

const DateInput = styled.input`
  width: 100%;
  height: 44px;
  border-radius: 8px;
  border: 1px solid ${(props) => (props.hasError ? theme.SECONDARY : theme.GREY_30)};
  padding: 0 14px;
  font-family: CeraPro;
  font-size: 14px;
  color: ${theme.GREY_80};
  background: white;
  &:focus {
    outline: none;
    border-color: ${theme.SECONDARY};
  }
`;

const Tab = styled.button`
  flex: 1;
  height: 40px;
  border: 1px solid ${(props) => (props.active ? theme.SECONDARY : theme.GREY_30)};
  background: ${(props) => (props.active ? `${theme.SECONDARY}15` : 'white')};
  color: ${(props) => (props.active ? theme.SECONDARY : theme.GREY_60)};
  font-family: CeraPro;
  font-size: 14px;
  font-weight: ${(props) => (props.active ? 600 : 400)};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.disabled ? 0.55 : 1)};
  &:first-child {
    border-radius: 8px 0 0 8px;
  }
  &:last-child {
    border-radius: 0 8px 8px 0;
    border-left: none;
  }
`;

// Compact segmented control for the content language (English / 日本語).
const LangPill = styled.button`
  height: 30px;
  padding: 0 14px;
  border: 1px solid ${(props) => (props.active ? theme.SECONDARY : theme.GREY_30)};
  background: ${(props) => (props.active ? `${theme.SECONDARY}15` : 'white')};
  color: ${(props) => (props.active ? theme.SECONDARY : theme.GREY_60)};
  font-family: CeraPro;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  &:first-child {
    border-radius: 8px 0 0 8px;
  }
  &:last-child {
    border-radius: 0 8px 8px 0;
    border-left: none;
  }
`;

// Neutral informational note (e.g. "poll type can't be changed").
const HintBanner = styled.div`
  background: ${theme.GREY_20};
  border: 1px solid ${theme.GREY_30};
  color: ${theme.GREY_80};
  border-radius: 8px;
  padding: 10px 14px;
  font-family: CeraPro;
  font-size: 13px;
  margin-bottom: 16px;
`;

// Warnings & errors render as plain red text (no bordered/background box).
const WarningText = styled.div`
  color: ${theme.SECONDARY};
  font-family: CeraPro;
  font-size: 13px;
  margin-bottom: 16px;
`;

// Convert a Date / ISO string to the YYYY-MM-DDTHH:mm string a datetime-local input
// expects, in the user's local timezone.
const toDatetimeLocal = (input) => {
  if (!input) return '';
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

// Treat the datetime-local string as local time and return a UTC ISO string for the API.
const fromDatetimeLocal = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

// Grey informational box used where an editor is unavailable (no match picked yet, or a
// section that isn't translated).
const noteBoxStyle = {
  borderRadius: 8,
  border: `1px solid ${theme.GREY_30}`,
  background: theme.GREY_10
};

const localTzLabel = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    return 'local time';
  }
})();

// An option is either a picked player ({ playerId, text, photo }) or a custom free-text
// answer ({ text, textJa }). A game-day poll may mix the two — player picks plus an answer
// like "No goals scored" — while non-game-day polls are free text only. On edit the admin
// endpoints don't populate options.player, so `o.player` is the bare id and `o.text` is the
// player's name — enough to seed the picker selection.
const seedOptions = (poll, type) => {
  const resolvedType = poll?.pollType || type || POLL_TYPE.NON_GAME_DAY;
  if (poll?.options?.length) {
    // Keep the option _id so translation-only edits can patch in place (votes intact), and
    // so a full options resend keeps each option's recorded votes linked.
    return poll.options.map((o) => {
      const playerId = o.player?._id || o.player || null;
      return playerId
        ? { _id: o._id, playerId, text: o.text, photo: o.player?.photo }
        : { _id: o._id, text: o.text, textJa: o.textJa || '' };
    });
  }
  // Empty defaults: game-day starts empty (tick players and/or add custom answers);
  // non-game-day with 2 text rows.
  return resolvedType === POLL_TYPE.GAME_DAY
    ? []
    : [
        { text: '', textJa: '' },
        { text: '', textJa: '' }
      ];
};

const PollForm = ({
  visible = true,
  teamId,
  mode = 'create',
  poll,
  onClose,
  onSaved,
  initialDraft,
  onDraftChange
}) => {
  const isEdit = mode === 'edit';

  const [pollType, setPollType] = useState(poll?.pollType || POLL_TYPE.NON_GAME_DAY);
  const [gameId, setGameId] = useState(
    poll?.game?._id || (typeof poll?.game === 'string' ? poll.game : '') || ''
  );
  const [question, setQuestion] = useState(poll?.question || '');
  // Multi-language content (UI only for now): Japanese question/options are collected and
  // kept in the shareable draft, but the submit payload is unchanged until the backend
  // supports translations. English stays the source of truth for structure.
  const [contentLang, setContentLang] = useState('en');
  const [questionJa, setQuestionJa] = useState(poll?.questionJa || '');
  const [rewardPoints, setRewardPoints] = useState(
    poll?.rewardPoints != null ? String(poll.rewardPoints) : String(POLL_DEFAULT_REWARD_POINTS)
  );
  const [isActive, setIsActive] = useState(poll ? poll.isActive : true);
  const [resultsVisible, setResultsVisible] = useState(poll ? poll.resultsVisible : true);
  const [startDate, setStartDate] = useState(toDatetimeLocal(poll?.startDate));
  const [endDate, setEndDate] = useState(toDatetimeLocal(poll?.endDate));
  const [options, setOptions] = useState(seedOptions(poll, poll?.pollType));
  // Only send options on update when actually edited — otherwise the backend would
  // reset every option's vote count on an unrelated change (toggle, reward, schedule).
  const [optionsTouched, setOptionsTouched] = useState(false);

  const [saving, setSaving] = useState(false);
  const [topError, setTopError] = useState(null);
  const [scheduleError, setScheduleError] = useState(null);

  // The panel stays mounted so its slide animation runs. Re-seed every field whenever
  // the panel reopens (visible flips false→true) so it reflects the current props.
  useEffect(() => {
    if (!visible) return;
    // A shared draft (from the URL) takes precedence over the poll/defaults so an
    // in-progress create/edit reopens exactly as it was left.
    const d = initialDraft || null;
    const resolvedType = d?.pollType || poll?.pollType || POLL_TYPE.NON_GAME_DAY;
    setPollType(resolvedType);
    setGameId(
      d?.gameId ?? (poll?.game?._id || (typeof poll?.game === 'string' ? poll.game : '') || '')
    );
    setQuestion(d?.question ?? poll?.question ?? '');
    setContentLang('en');
    setQuestionJa(d?.questionJa ?? poll?.questionJa ?? '');
    setRewardPoints(
      d?.rewardPoints != null
        ? String(d.rewardPoints)
        : poll?.rewardPoints != null
          ? String(poll.rewardPoints)
          : String(POLL_DEFAULT_REWARD_POINTS)
    );
    setIsActive(d ? !!d.isActive : poll ? poll.isActive : true);
    setResultsVisible(d ? !!d.resultsVisible : poll ? poll.resultsVisible : true);
    setStartDate(d?.startDate ?? toDatetimeLocal(poll?.startDate));
    setEndDate(d?.endDate ?? toDatetimeLocal(poll?.endDate));
    // Draft options are already in the right shape (player or text); keep them verbatim.
    setOptions(d?.options?.length ? d.options : seedOptions(poll, resolvedType));
    // Seeded from a draft → treat options as edited so they persist on save.
    setOptionsTouched(!!d);
    setSaving(false);
    setTopError(null);
    setScheduleError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // Mirror the live form state into the URL (debounced) so the open draft is shareable.
  useEffect(() => {
    if (!visible || !onDraftChange) return;
    const draft = {
      pollType,
      gameId,
      question,
      questionJa,
      rewardPoints,
      isActive,
      resultsVisible,
      startDate,
      endDate,
      options
    };
    const t = setTimeout(() => onDraftChange(draft), 300);
    return () => clearTimeout(t);
  }, [
    visible,
    onDraftChange,
    pollType,
    gameId,
    question,
    questionJa,
    rewardPoints,
    isActive,
    resultsVisible,
    startDate,
    endDate,
    options
  ]);

  const isGameDay = pollType === POLL_TYPE.GAME_DAY;
  const isNonGameDay = pollType === POLL_TYPE.NON_GAME_DAY;

  // Custom (free-text) options paired with their index in `options`, so the text editors
  // patch the right entry — a game-day list interleaves player picks with custom answers.
  const customOptions = useMemo(
    () => options.map((opt, index) => ({ opt, index })).filter(({ opt }) => !opt.playerId),
    [options]
  );
  // Options that would actually be submitted: a picked player, or text that isn't blank.
  const filledOptionCount = options.filter(
    (o) => o.playerId || (o.text && o.text.trim().length > 0)
  ).length;

  // Switching type changes what the option list starts from, so reset it: game-day opens
  // empty (tick players and/or add custom answers), non-game-day with blank text rows.
  const switchType = (type) => {
    if (type === pollType) return;
    setPollType(type);
    setOptions(seedOptions(null, type));
    setOptionsTouched(false);
    setContentLang('en');
  };

  // Game-day: the picker is handed the whole option list and returns it with only the player
  // entries added/removed — custom free-text answers in the list come back untouched.
  const handlePlayerChange = (next) => {
    setOptionsTouched(true);
    setOptions(next);
  };

  const handleOptionText = (i, value) => {
    setOptionsTouched(true);
    setOptions((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], text: value };
      return next;
    });
  };

  // Japanese texts are UI-only content — they don't touch the vote-resetting options
  // payload, so they intentionally don't mark options as edited.
  const handleOptionTextJa = (i, value) => {
    setOptions((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], textJa: value };
      return next;
    });
  };

  const addOption = () => {
    if (options.length >= POLL_MAX_OPTIONS) return;
    setOptionsTouched(true);
    setOptions((prev) => [...prev, { text: '', textJa: '' }]);
  };

  // Non-game-day polls keep the minimum number of text rows on screen; a game-day custom
  // answer is always optional, since the player picks can supply the whole option list.
  const canRemoveOption = isGameDay || options.length > POLL_MIN_OPTIONS;

  const removeOption = (i) => {
    if (!canRemoveOption) return;
    setOptionsTouched(true);
    setOptions((prev) => prev.filter((_, idx) => idx !== i));
  };

  const validate = () => {
    if (!question.trim()) return 'Question is required';
    if (rewardPoints === '' || Number(rewardPoints) < 0)
      return 'Reward points must be 0 or greater';
    const norm = (s) => s.trim().toLowerCase();
    if (isGameDay) {
      if (!gameId) return 'Game-day polls need a match';
      const picked = options.filter((o) => o.playerId);
      const custom = options.filter((o) => !o.playerId && o.text && o.text.trim().length > 0);
      const total = picked.length + custom.length;
      if (total < POLL_MIN_OPTIONS)
        return `Add at least ${POLL_MIN_OPTIONS} options — tick players, add custom options, or both`;
      if (total > POLL_MAX_OPTIONS) return `A poll can have at most ${POLL_MAX_OPTIONS} options`;
      if (new Set(picked.map((o) => o.playerId)).size !== picked.length)
        return 'Duplicate options are not allowed. Please select a unique player.';
      if (new Set(custom.map((o) => norm(o.text))).size !== custom.length)
        return 'Duplicate options are not allowed. Please enter a unique option.';
      // A custom answer typed as a ticked player's name would render as two identical choices
      // in the app — the backend only dedupes player-vs-player and text-vs-text.
      const pickedNames = new Set(picked.map((o) => norm(o.text || '')));
      if (custom.some((o) => pickedNames.has(norm(o.text))))
        return 'A custom option matches a selected player. Please enter a different option.';
      const gameDayJa = custom.map((o) => o.textJa).filter((t) => t && t.trim());
      if (new Set(gameDayJa.map(norm)).size !== gameDayJa.length)
        return 'Duplicate Japanese options are not allowed. Please enter a unique option.';
    } else {
      const trimmed = options.filter((o) => o.text && o.text.trim().length > 0);
      if (trimmed.length < POLL_MIN_OPTIONS)
        return `Provide at least ${POLL_MIN_OPTIONS} non-empty options`;
      if (trimmed.length > POLL_MAX_OPTIONS)
        return `A poll can have at most ${POLL_MAX_OPTIONS} options`;
      if (new Set(trimmed.map((o) => norm(o.text))).size !== trimmed.length)
        return 'Duplicate options are not allowed. Please enter a unique option.';
      const jaTexts = trimmed.map((o) => o.textJa).filter((t) => t && t.trim());
      if (new Set(jaTexts.map(norm)).size !== jaTexts.length)
        return 'Duplicate Japanese options are not allowed. Please enter a unique option.';
      if (!startDate || !endDate) return 'Start and end dates are required';
      if (new Date(endDate) <= new Date(startDate)) return 'End date must be after start date';
    }
    return null;
  };

  const handleSubmit = async () => {
    const localErr = validate();
    if (localErr) {
      setTopError(localErr);
      return;
    }

    setTopError(null);
    setScheduleError(null);
    setSaving(true);

    // Send the existing option _id on edit so the backend keeps its prior votes linked
    // instead of re-minting the id and wiping the result distribution. A picked player ships
    // as { playerId } (its text/photo are derived server-side); anything else as free text.
    const validOptions = options
      .filter((o) => o.playerId || (o.text && o.text.trim().length > 0))
      .map((o) => {
        const opt = o.playerId ? { playerId: o.playerId } : { text: o.text.trim() };
        if (!o.playerId && o.textJa && o.textJa.trim()) opt.textJa = o.textJa.trim();
        if (o._id) opt._id = o._id;
        return opt;
      });

    let res;
    if (isEdit) {
      const payload = {
        id: poll._id,
        question: question.trim(),
        questionJa: questionJa.trim(),
        rewardPoints: Number(rewardPoints),
        isActive,
        resultsVisible
      };
      // Only resend options when edited. Each option carries its _id, so the backend keeps
      // the votes of the options that already existed.
      if (optionsTouched) payload.options = validOptions;
      // Otherwise ship Japanese texts as an in-place patch — ids and votes stay intact.
      // Player options are skipped: their text is a lineup name and is never translated.
      else {
        const translations = options
          .filter((o) => o._id && !o.playerId)
          .map((o) => ({ optionId: o._id, textJa: (o.textJa || '').trim() }));
        if (translations.length) payload.optionTranslations = translations;
      }
      // pollType & gameId are immutable; game-day polls reject manual dates.
      if (poll.pollType === POLL_TYPE.NON_GAME_DAY) {
        payload.startDate = fromDatetimeLocal(startDate);
        payload.endDate = fromDatetimeLocal(endDate);
      }
      res = await Api.put('/admin/polls/update', payload, null, { silent: true });
    } else {
      const payload = {
        question: question.trim(),
        questionJa: questionJa.trim() || undefined,
        teamId,
        pollType,
        options: validOptions,
        rewardPoints: Number(rewardPoints),
        isActive,
        resultsVisible
      };
      if (isGameDay) {
        payload.gameId = gameId;
      } else {
        payload.startDate = fromDatetimeLocal(startDate);
        payload.endDate = fromDatetimeLocal(endDate);
      }
      res = await Api.post('/admin/polls/create', payload, null, { silent: true });
    }

    setSaving(false);

    if (res && res.err && res.status) {
      const err = res.err;
      if (res.status === 409) {
        // The backend names what it overlaps — another poll, or a game-day poll before kickoff.
        setScheduleError(err || 'Schedule overlaps with an existing poll');
        return;
      }
      if (res.status === 404) {
        setTopError('This poll no longer exists — it may have been deleted.');
        return;
      }
      if (res.status === 403) {
        setTopError("You don't have access to manage polls on this channel.");
        return;
      }
      if (res.status === 400) {
        if (/start|end|schedule|date/i.test(err)) setScheduleError(err);
        else setTopError(err);
        return;
      }
      setTopError(err || 'Something went wrong. Please try again.');
      return;
    }

    if (res?.data || res?.msg) {
      onSaved(res.data || null);
      onClose();
    }
  };

  const headerText = useMemo(() => {
    if (isEdit) return `Edit ${poll?.pollType === POLL_TYPE.GAME_DAY ? 'game-day' : 'non-game-day'} poll`;
    return isGameDay ? 'Create game-day poll' : 'Create non-game-day poll';
  }, [isEdit, isGameDay, poll]);

  const optionsValid = filledOptionCount >= POLL_MIN_OPTIONS;
  const canSubmit =
    !saving &&
    question.trim().length > 0 &&
    optionsValid &&
    (isGameDay ? !!gameId : !!startDate && !!endDate);

  const hasVotes = isEdit && (poll?.totalSubmissions || 0) > 0;

  // Japanese translation progress — the question plus every custom option that has English
  // text. Player options are lineup names and are never translated.
  const isJa = contentLang === 'ja';
  const jaSlots = [
    questionJa,
    ...customOptions.filter(({ opt }) => opt.text && opt.text.trim()).map(({ opt }) => opt.textJa)
  ];
  const jaDone = jaSlots.filter((v) => v && v.trim()).length;
  const jaTotal = jaSlots.length;

  // Indices of custom free-text options that duplicate an earlier one (trimmed,
  // case-insensitive, in the currently-viewed language). Player options carry a lineup name
  // that can't be typed, so they're skipped. Drives the red border + inline warning.
  const duplicateOptionIdx = useMemo(() => {
    const dupes = new Set();
    const seen = new Map();
    options.forEach((o, i) => {
      if (o.playerId) return;
      const key = ((isJa ? o.textJa : o.text) || '').trim().toLowerCase();
      if (!key) return;
      if (seen.has(key)) {
        dupes.add(i);
        dupes.add(seen.get(key));
      } else {
        seen.set(key, i);
      }
    });
    return dupes;
  }, [options, isJa]);
  const hasDuplicateOptions = duplicateOptionIdx.size > 0;

  return (
    <Sidepanel visible={visible} wid={720} pad="0" closeModal={() => {}}>
      <ContentHeader text={headerText} closePanel={onClose} />
      <Col wid="100%" pad="20px 24px">
        {/* Step 1: poll type — create only; type is immutable after creation. */}
        {!isEdit && (
          <Row noFlex marg="0 0 16px">
            <Tab type="button" active={isNonGameDay} onClick={() => switchType(POLL_TYPE.NON_GAME_DAY)}>
              Non-game day
            </Tab>
            <Tab type="button" active={isGameDay} onClick={() => switchType(POLL_TYPE.GAME_DAY)}>
              Game day
            </Tab>
          </Row>
        )}

        {isEdit && (
          <HintBanner>
            Poll type {poll?.pollType === POLL_TYPE.GAME_DAY ? 'and match ' : ''}can&apos;t be
            changed — delete and recreate to switch.
          </HintBanner>
        )}

        {isEdit && hasVotes && (
          <WarningText>
            This poll already has {poll.totalSubmissions} submission
            {poll.totalSubmissions === 1 ? '' : 's'}. Editing the options will reset all vote
            counts.
          </WarningText>
        )}

        {topError && <WarningText>{topError}</WarningText>}

        {/* Content language — Japanese is collected in the UI only for now; the save
            payload is unchanged until the backend supports translations. */}
        <Row between center noFlex marg="0 0 12px" gap="12px">
          <Col noFlex style={{ minWidth: 0 }}>
            <Text h4 marg="0">
              Content language
            </Text>
            <Text c2 col={theme.GREY_60} marg="2px 0 0">
              Japanese is optional — empty fields fall back to English in the fan app.
            </Text>
          </Col>
          <Row noFlex center>
            <LangPill type="button" active={!isJa} onClick={() => setContentLang('en')}>
              English
            </LangPill>
            <LangPill type="button" active={isJa} onClick={() => setContentLang('ja')}>
              日本語 · {jaDone}/{jaTotal}
            </LangPill>
          </Row>
        </Row>

        {isJa && (
          <HintBanner>
            Translating to Japanese. Options, schedule and settings are managed on the English
            tab — here you only translate the text. English text is shown as the placeholder.
          </HintBanner>
        )}

        <Col wid="100%" marg="0 0 16px">
          <Input
            title={isJa ? 'Question (日本語)' : 'Question *'}
            placeholder={isJa ? question || '質問を入力してください' : 'Ask the fans something'}
            value={isJa ? questionJa : question}
            onChange={(e) => (isJa ? setQuestionJa(e.target.value) : setQuestion(e.target.value))}
            wid="100%"
          />
        </Col>

        {/* Game-day: match picker, no date inputs (timing is automatic). */}
        {isGameDay && (
          <Col wid="100%" marg="0 0 16px">
            <Text h4 marg="0 0 8px">
              Match *
            </Text>
            <PollGamePicker
              teamId={teamId}
              value={gameId}
              onChange={setGameId}
              hasError={false}
              disabled={isEdit}
              refreshKey={visible}
            />
            <Text c2 col={theme.GREY_60} marg="8px 0 0">
              Game-day polls open before kickoff, close at kickoff and show results after — timing
              is driven automatically by the match status.
            </Text>
          </Col>
        )}

        {/* Non-game-day: scheduled date window. */}
        {isNonGameDay && (
          <>
            <Row wid="100%" marg="0 0 8px" gap="12px">
              <Col flex={1}>
                <Text h4 marg="0 0 8px">
                  Start *
                </Text>
                <DateInput
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  hasError={!!scheduleError}
                />
              </Col>
              <Col flex={1}>
                <Text h4 marg="0 0 8px">
                  End *
                </Text>
                <DateInput
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  hasError={!!scheduleError}
                />
              </Col>
            </Row>
            <Text c2 col={theme.GREY_60} marg="0 0 16px">
              Times shown in your local time ({localTzLabel}); stored in UTC.
            </Text>
          </>
        )}

        {scheduleError && <WarningText>{scheduleError}</WarningText>}

        <Col wid="100%" marg="0 0 16px">
          <Input
            title="Reward points"
            type="number"
            placeholder={String(POLL_DEFAULT_REWARD_POINTS)}
            value={rewardPoints}
            onChange={(e) => setRewardPoints(e.target.value)}
            wid="100%"
          />
        </Col>

        {/* Game-day: tick players from the lineup/squad. Custom answers are added in the
            block below — players and custom options share the same 2–6 option limit. */}
        {isGameDay && (
          <Col wid="100%" marg="0 0 16px">
            <Text h4 marg="0 0 8px">
              Players
            </Text>
            {isJa ? (
              <Col wid="100%" pad="12px 14px" style={noteBoxStyle}>
                <Text c2 col={theme.GREY_60}>
                  Player options are names from the match lineup — they aren&apos;t translated.
                  Translate the question and any custom options below.
                </Text>
              </Col>
            ) : gameId ? (
              <PollPlayerPicker
                teamId={teamId}
                gameId={gameId}
                value={options}
                onChange={handlePlayerChange}
                maxSelected={POLL_MAX_OPTIONS}
                hasError={false}
              />
            ) : (
              <Col wid="100%" pad="12px 14px" style={noteBoxStyle}>
                <Text c2 col={theme.GREY_60}>
                  Select a match above to load its players.
                </Text>
              </Col>
            )}
          </Col>
        )}

        {/* Custom free-text options — the only option editor for a non-game-day poll, and the
            optional non-player answers (e.g. "No goals scored") for a game-day one. Hidden on
            the Japanese tab of a game-day poll until there is something to translate. */}
        {(!isGameDay || !isJa || customOptions.length > 0) && (
          <Col wid="100%" marg="0 0 16px">
            <Row between center marg="0 0 8px">
              <Text h4>
                {isGameDay ? 'Custom options' : `Options (${POLL_MIN_OPTIONS}–${POLL_MAX_OPTIONS})`}
                {isJa ? ' — 日本語' : ''}
              </Text>
              {!isJa && (
                <Button
                  text={isGameDay ? 'Add custom option' : 'Add option'}
                  onClick={addOption}
                  bordered
                  disabled={options.length >= POLL_MAX_OPTIONS}
                  style={{ width: 'fit-content', padding: '4px 12px', fontSize: 13 }}
                />
              )}
            </Row>

            {isGameDay && !isJa && (
              <Text c2 col={theme.GREY_60} marg="0 0 8px">
                Optional — an answer that isn&apos;t a player, e.g. &quot;No goals scored&quot;.
                Using {filledOptionCount} of {POLL_MAX_OPTIONS} options (players included).
              </Text>
            )}

            {customOptions.map(({ opt, index }, pos) => (
              <Row key={opt._id || `new-${index}`} center marg="0 0 8px">
                <Col flex={1} marg="0 10px 0 0">
                  <Input
                    placeholder={isJa ? opt.text || `オプション ${pos + 1}` : `Option ${pos + 1}`}
                    value={isJa ? opt.textJa || '' : opt.text}
                    onChange={(e) =>
                      isJa
                        ? handleOptionTextJa(index, e.target.value)
                        : handleOptionText(index, e.target.value)
                    }
                    error={duplicateOptionIdx.has(index)}
                    wid="100%"
                    noTitle
                  />
                </Col>
                {!isJa && (
                  <Icon
                    type="close"
                    col={canRemoveOption ? theme.GREY_60 : theme.GREY_20}
                    onClick={() => removeOption(index)}
                    style={{ cursor: canRemoveOption ? 'pointer' : 'not-allowed' }}
                  />
                )}
              </Row>
            ))}
            {hasDuplicateOptions && (
              <WarningText style={{ margin: '4px 0 0' }}>
                {isJa
                  ? 'Duplicate Japanese options are not allowed. Please enter a unique option.'
                  : 'Duplicate options are not allowed. Please enter a unique option.'}
              </WarningText>
            )}
          </Col>
        )}

        <Row between center marg="0 0 16px">
          <Col noFlex>
            <Text>Active</Text>
            <Text c2 col={theme.GREY_60}>
              Inactive polls are hidden from fans.
            </Text>
          </Col>
          <Switch activated={isActive} onClick={() => setIsActive((v) => !v)} />
        </Row>

        <Row between center marg="0 0 24px">
          <Col noFlex>
            <Text>Show results</Text>
            <Text c2 col={theme.GREY_60}>
              Reveal the vote breakdown to fans.
            </Text>
          </Col>
          <Switch activated={resultsVisible} onClick={() => setResultsVisible((v) => !v)} />
        </Row>

        <Row
          center
          justifyContent="flex-end"
          pad="16px 0 8px"
          style={{ borderTop: `1px solid ${theme.GREY_20}` }}
        >
          <Button
            text="Cancel"
            onClick={onClose}
            bordered
            marg="0 12px 0 0"
            style={{ width: 'fit-content', opacity: 0.8 }}
          />
          <Button
            text={saving ? 'Saving...' : isEdit ? 'Save changes' : 'Create poll'}
            onClick={handleSubmit}
            disabled={!canSubmit}
            bg={theme.SECONDARY}
            style={{ width: 'fit-content' }}
          />
        </Row>
      </Col>
    </Sidepanel>
  );
};

export default PollForm;
